import React from "react";
import { Modal, Box, Typography, Stack, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import colors from "../../../../utils/colors";
import { SslType } from "../../../../utils/constants";
import { GhostButton, PrimaryButton } from "../../../common/Button";
import SslModeFlowDiagram from "./SslModeFlowDiagram";
import {
  certsBodySmallSx,
  certsBodySecondarySx,
  certsSectionTitleSx,
  certsOverlineSx,
} from "./certsTypography";
import {
  CERTS_HEADING_BG,
  certsCardShellSx,
} from "./certsLayout";

const INDIGO = colors.primary[300];
const INDIGO_INK = colors.primary[500];
const INDIGO_SOFT = "rgba(99,113,247,0.08)";
const INDIGO_BORDER = "rgba(99,113,247,0.22)";

/** Tone chips — same soft fills as ModeLadder on SSLTLSPage (not neon). */
const MODE_CHIPS = [
  {
    id: SslType.OFF,
    label: "InSecure",
    tone: {
      bg: "rgba(208,4,22,0.08)",
      fg: colors.red[200],
      bd: "rgba(208,4,22,0.22)",
      active: colors.red[200],
    },
  },
  {
    id: SslType.FLEXIBLE,
    label: "Adaptive",
    tone: {
      bg: "rgba(223,180,0,0.12)",
      fg: "#7a5b10",
      bd: "rgba(223,180,0,0.32)",
      active: colors.yellow[200],
    },
  },
  {
    id: SslType.FULL,
    label: "Adaptive E2E",
    tone: {
      bg: "rgba(23,143,79,0.08)",
      fg: colors.green[300],
      bd: "rgba(23,143,79,0.28)",
      active: colors.green[300],
    },
  },
  {
    id: SslType.FULL_STRICT,
    label: "Advanced E2E",
    tone: {
      bg: INDIGO_SOFT,
      fg: INDIGO_INK,
      bd: INDIGO_BORDER,
      active: colors.primary[400],
    },
  },
];

/**
 * Compare-modes explainer — certs theme shell + Imperva-style traffic SVG.
 */
export default function SslModeCompareModal({
  open,
  onClose,
  mode,
  modeTitle,
  modeDesc,
  onModeChange,
}) {
  return (
    <Modal
      open={!!open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(17, 17, 17, 0.45)" } },
      }}
      aria-labelledby="ssl-mode-compare-title"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", sm: 920, md: 980 },
          maxWidth: "98vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          borderRadius: "12px",
          border: `1px solid ${colors.neutral[200]}`,
          boxShadow:
            "0 24px 60px rgba(17,17,17,0.14), 0 4px 12px rgba(17,17,17,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Header — matches certs card heading bar */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            backgroundColor: CERTS_HEADING_BG,
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: `1px solid ${colors.neutral[200]}`,
              color: INDIGO,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                ...certsOverlineSx,
                color: colors.neutral[600],
                mb: 0.25,
              }}
            >
              Encryption modes
            </Typography>
            <Typography
              id="ssl-mode-compare-title"
              sx={{ ...certsSectionTitleSx, fontSize: 17, lineHeight: "24px" }}
            >
              How Layerd handles SSL
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              color: colors.neutral[700],
              border: `1px solid ${colors.neutral[200]}`,
              borderRadius: "8px",
              backgroundColor: "#fff",
              width: 32,
              height: 32,
              "&:hover": { backgroundColor: colors.neutral[50], borderColor: colors.neutral[300] },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            overflow: "auto",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Typography sx={{ ...certsBodySecondarySx, mb: 2, maxWidth: 560 }}>
            See how traffic is encrypted between visitors, Layerd, and your origin for each mode.
          </Typography>

          {/* Mode switcher — ladder tone language */}
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
            {MODE_CHIPS.map((chip) => {
              const selected = chip.id === mode;
              const t = chip.tone;
              return (
                <Box
                  key={chip.id}
                  component="button"
                  type="button"
                  onClick={() => onModeChange?.(chip.id)}
                  sx={{
                    border: `1.5px solid ${selected ? t.active : colors.neutral[200]}`,
                    backgroundColor: selected ? t.bg : "#fff",
                    color: selected ? t.fg : colors.neutral[800],
                    borderRadius: "8px",
                    px: 1.75,
                    py: 0.85,
                    fontSize: 13,
                    lineHeight: "18px",
                    fontWeight: selected ? 600 : 500,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    boxShadow: selected ? "0 1px 2px rgba(17,17,17,0.05)" : "none",
                    transition: "border-color 140ms, background-color 140ms, box-shadow 140ms",
                    "&:hover": {
                      borderColor: selected ? t.active : colors.neutral[400],
                      backgroundColor: selected ? t.bg : colors.neutral[50],
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${INDIGO}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  {chip.label}
                </Box>
              );
            })}
          </Stack>

          <Box
            sx={{
              ...certsCardShellSx,
              p: 0,
              overflow: "hidden",
              borderRadius: "12px",
            }}
          >
            <SslModeFlowDiagram mode={mode} />
          </Box>

          {(modeTitle || modeDesc) && (
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                mt: 2.5,
                p: "14px 16px",
                borderRadius: "10px",
                backgroundColor: INDIGO_SOFT,
                border: `1px solid ${INDIGO_BORDER}`,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: INDIGO, mt: 0.15, flexShrink: 0 }} />
              <Typography sx={{ ...certsBodySmallSx, lineHeight: "20px" }}>
                {modeTitle && (
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {modeTitle}.{" "}
                  </Box>
                )}
                <Box component="span" sx={{ color: colors.neutral[800] }}>
                  {modeDesc}
                </Box>
              </Typography>
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2,
            borderTop: `1px solid ${colors.neutral[200]}`,
            backgroundColor: "#fff",
            flexShrink: 0,
          }}
        >
          <GhostButton onClick={onClose} sx={{ minWidth: 96, height: 36 }}>
            Close
          </GhostButton>
          <PrimaryButton onClick={onClose} sx={{ minWidth: 120, height: 36 }}>
            Got it
          </PrimaryButton>
        </Stack>
      </Box>
    </Modal>
  );
}
